import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { ProviderLogo } from "@/components/ProviderLogo";
import { useLang, useT } from "@/lib/i18n";
import { INTEGRATION_CATEGORIES, INTEGRATIONS_TOTAL } from "@/lib/integrationsCatalog";

export default function IntegrationsCatalog() {
  const t = useT();
  const { lang } = useLang();
  const l = (obj) => obj[lang] || obj.en || obj.pt;
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    document.title = `${t("integrations.page.titleTag").replace("{n}", INTEGRATIONS_TOTAL)} · Ratchet`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const visible = useMemo(
    () => (filter === "all" ? INTEGRATION_CATEGORIES : INTEGRATION_CATEGORIES.filter((c) => c.id === filter)),
    [filter]
  );

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="integrations-page">
      <MarketingNav />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-12">
        <div className="overline mb-4">{t("integrations.page.overline").replace("{n}", INTEGRATIONS_TOTAL)}</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-4xl leading-[1.02]">
          {t("integrations.page.title")}
        </h1>
        <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">{t("integrations.page.subtitle")}</p>

        <div className="mt-10 flex flex-wrap gap-2" role="tablist" aria-label={t("integrations.page.filter")}>
          <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">
            {t("integrations.page.all")} <span className="text-white/40">{INTEGRATIONS_TOTAL}</span>
          </FilterPill>
          {INTEGRATION_CATEGORIES.map((c) => (
            <FilterPill key={c.id} active={filter === c.id} onClick={() => setFilter(c.id)} testid={`filter-${c.id}`}>
              {l(c.title)} <span className="text-white/40">{c.items.length}</span>
            </FilterPill>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 space-y-16">
        {visible.map((cat) => (
          <div key={cat.id} id={cat.id} data-testid={`category-${cat.id}`}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-white/10 pb-4">
              <div>
                <h2 className="font-display font-bold text-2xl tracking-tight">
                  {l(cat.title)} <span className="text-white/30 font-mono text-base align-middle">{cat.items.length}</span>
                </h2>
                <p className="text-sm text-white/60 mt-2 max-w-2xl">{l(cat.desc)}</p>
              </div>
            </div>
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {cat.items.map((it) => (
                <li
                  key={it.key}
                  className="surface p-4 flex items-center gap-3 hover:border-primary/60 transition-colors"
                  data-testid={`integration-${it.key}`}
                >
                  <ProviderLogo type={it.key} size={44} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white truncate" title={it.label}>{it.label}</div>
                    {it.comingSoon ? (
                      <div className="text-[11px] font-mono text-amber-400 mt-0.5">{t("integrations.page.soon")}</div>
                    ) : (
                      <div className="text-[11px] font-mono text-white/40 mt-0.5">{t("integrations.page.ready")}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display font-black text-3xl tracking-tighter">{t("integrations.page.missingTitle")}</h2>
            <p className="text-white/60 mt-3 max-w-lg">{t("integrations.page.missingDesc")}</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/signup" className="btn-primary" data-testid="integrations-cta-signup">
              {t("landing.cta.signup")} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:owners@myratchet.com?subject=Ratchet%20integration%20request" className="btn-ghost" data-testid="integrations-cta-request">
              {t("integrations.page.request")} <Mail className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 text-xs font-mono border transition-colors ${
        active ? "border-primary bg-primary/10 text-white" : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}
